import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { Layout } from "Layout";

import { CustomFields } from "components/CustomFields/CustomFields";
import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { SectionHeading } from "components/SectionHeading";
import {
  FrmNumber,
  FrmLocation,
  FrmComment,
  FrmDepartment,
} from "components/formFields/frm";
import { FrmJob } from "components/formFields/frm/FrmJob";
import { FrmSignature } from "components/formFields/frm/FrmSignature";
import { FrmWorkCenter } from "components/formFields/frm/FrmWorkCenter";
import { DateTimePicker } from "components/html/Input/DateTimePicker";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";
import { WarningModal } from "components/utility/WarningModal";

import { i18n } from "services/i18nService";
import {
  updateInputTotals,
  INPUT_ZERO_TOTALS,
} from "services/sosInventoryService/build/afterTouchInputLine";
import { updateInputs } from "services/sosInventoryService/productionTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";

import { useCustomFieldDefinitions } from "hooks/useCustomFieldDefinitions";
import { useErrors } from "hooks/useErrors";
import { useInputOutputTransaction } from "hooks/useInputOutputTransaction";
import { useItems } from "hooks/useItems";
import { useLineHandler } from "hooks/useLineHandler";
import { useLocations } from "hooks/useLocations";

import { LineItems as InputLineItems } from "views/Builds/Build//Input/LineItems";
import { BottomWidgets as OutputBottomWidgets } from "views/Builds/Build/Output/BottomWidgets";
import { LineItems as OutputLineItems } from "views/Builds/Build/Output/LineItems";
import { updateTransaction } from "views/Builds/Build/controller";

import { theme } from "SosTheme";
import { OBJECT_TYPES } from "appConstants";
import { EMPTY_OUTPUT_LINE_ITEM, EMPTY_INPUT_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.BUILD.fullString;

export function Build(props) {
  const { id, onClose, newFromId, newFromObjectType, newFromObjectTypeLine } =
    props;

  // TRANSACTION STATE
  const [record, setRecord] = useState();
  const [outputLines, outputLineHandler] = useLineHandler();
  const [inputLines, inputLineHandler] = useLineHandler();
  const [relatedRecords, setRelatedRecords] = useState({});
  const [keepEditingRecord, setKeepEditingRecord] = useState(null);

  // UI STATE
  const [inputTotals, setInputTotals] = useState(INPUT_ZERO_TOTALS);
  const { errors, setErrors, isInError } = useErrors();
  const [clearOutputLines, setClearOutputLines] = useState(false);
  const [clearInputLines, setClearInputLines] = useState(false);
  const [itemAdded, setItemAdded] = useState(0);
  const loadingState = useSelector((state) => state.loading.editModal);
  const { items, itemsCount } = useItems(null, itemAdded);

  const locations = useLocations();

  const transactionCustomFieldDefs = useCustomFieldDefinitions(OBJECT_TYPE);
  const departmentEnabled = useSelector(
    (state) => state.userCompanySettings.settings.departmentEnabled
  );

  const CURRENT_STATE = { record, outputLines, inputLines, relatedRecords };

  // UPDATERS FOR CONTROLLER TO UPDATE STATE
  const UPDATERS = useMemo(
    () => ({
      setRecord,
      outputLineHandler,
      inputLineHandler,
      setRelatedRecords,
      setErrors,
    }),
    [outputLineHandler, inputLineHandler, setErrors]
  );

  useInputOutputTransaction(
    OBJECT_TYPE,
    id,
    setRecord,
    setRelatedRecords,
    outputLineHandler,
    inputLineHandler,
    transactionCustomFieldDefs,
    newFromObjectType,
    newFromObjectTypeLine,
    newFromId,
    setErrors,
    keepEditingRecord
  );

  useEffect(() => updateInputTotals(inputLines, setInputTotals), [inputLines]);

  // HANDLE ALL USER INPUT
  function handleInputFieldChange(field, newValue) {
    setPageDirty();
    updateTransaction(field, newValue, CURRENT_STATE, UPDATERS);
  }

  async function onUpdateInputs() {
    const newInputs = await updateInputs(
      outputLines,
      record.date,
      relatedRecords.location
    );
    if (newInputs) {
      inputLineHandler({ type: "set", lines: newInputs });
    }
  }

  function doClearOutputLines() {
    setPageDirty();
    outputLineHandler({
      type: "clear",
      blankLine: EMPTY_OUTPUT_LINE_ITEM[OBJECT_TYPE],
    });
    setClearOutputLines(false);
  }

  function doClearInputLines() {
    setPageDirty();
    inputLineHandler({
      type: "clear",
      blankLine: EMPTY_INPUT_LINE_ITEM[OBJECT_TYPE],
    });
    setClearInputLines(false);
  }

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        onSaveAndKeepEditing={setKeepEditingRecord}
        objectType={OBJECT_TYPE}
        relatedRecords={relatedRecords}
        outputs={outputLines}
        inputs={inputLines}
        setErrors={setErrors}
        errors={errors}
        text={record?.number}
        handleClose={onClose}
      />
      {loadingState && <Loading />}
      <ObjectEditDialogContent>
        <Layout
          pageTitle={
            id
              ? `${i18n("objectType.build.Sentence")} ${record?.number || ""}`
              : i18n("objectType.build.New")
          }
        >
          <div id="B" style={{ margin: theme.spacing(2) }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2" }}>
                    <DateTimePicker
                      value={record.date}
                      name="date"
                      label={i18n("frmLabel.DateTime")}
                      onValueChange={handleInputFieldChange}
                      error={isInError("date")}
                      dataTesting="date"
                    />
                  </div>
                  <div style={{ gridColumn: "2 / 2" }}>
                    <FrmNumber
                      recordId={record.id}
                      label={i18n("build.labelNumber")}
                      value={record.number}
                      onValueChange={handleInputFieldChange}
                      error={isInError("number")}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2" }}>
                    <FrmLocation
                      value={record.location}
                      onValueChange={handleInputFieldChange}
                      locations={locations}
                      width="25rem"
                      error={isInError("location")}
                    />
                  </div>
                </FrmFieldGrid>
                <VSpace space={2} />
                <SectionHeading>{i18n("global.Output")}</SectionHeading>
                <VSpace space={1} />
                <OutputLineItems
                  record={record}
                  objectType={OBJECT_TYPE}
                  relatedRecords={relatedRecords}
                  lines={outputLines}
                  items={items}
                  itemsCount={itemsCount}
                  addItem={() => setItemAdded((prev) => prev + 1)}
                  lineHandler={outputLineHandler}
                  lineItemsErrors={errors.output}
                  setErrors={setErrors}
                />
                <VSpace space={1} />
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <OutputBottomWidgets
                    updateInputs={onUpdateInputs}
                    output={outputLines ? outputLines[0] : null}
                    handleInputFieldChange={handleInputFieldChange}
                    autoSerialLotNumbering={record.autoSerialLotNumbering}
                  />
                </div>

                <VSpace space={1} />

                <SectionHeading>{i18n("global.Inputs")}</SectionHeading>

                <VSpace space={1} />

                <InputLineItems
                  record={record}
                  objectType={OBJECT_TYPE}
                  output={outputLines ? outputLines[0] : null}
                  relatedRecords={relatedRecords}
                  lines={inputLines}
                  totals={inputTotals}
                  items={items}
                  itemsCount={itemsCount}
                  addItem={() => setItemAdded((prev) => prev + 1)}
                  lineHandler={inputLineHandler}
                  setClearLines={setClearInputLines}
                  lineItemsErrors={errors.input}
                  setErrors={setErrors}
                />

                <VSpace space={3} />

                <FrmFieldGrid gridAutoFlow="dense">
                  <FrmJob
                    maxWidth="20rem"
                    value={record.job}
                    onValueChange={handleInputFieldChange}
                  />
                  <FrmWorkCenter
                    maxWidth="20rem"
                    value={record.workcenter}
                    onValueChange={handleInputFieldChange}
                  />
                  {departmentEnabled && (
                    <div style={{ gridColumn: "2 / 2" }}>
                      <FrmDepartment
                        maxWidth="20rem"
                        value={record.department}
                        onValueChange={handleInputFieldChange}
                      />
                    </div>
                  )}
                </FrmFieldGrid>
                <FrmFieldGrid gridAutoFlow="dense">
                  <div style={{ gridColumn: "1 / 2", maxWidth: "70ch" }}>
                    <FrmComment
                      value={record.comment}
                      onValueChange={handleInputFieldChange}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / 2", maxWidth: "70ch" }}>
                    <FrmSignature
                      objectType={OBJECT_TYPE}
                      id={record.id}
                      hasSignature={record.hasSignature}
                    />
                  </div>
                </FrmFieldGrid>

                <CustomFields
                  customFieldDefinitions={transactionCustomFieldDefs}
                  customFields={record.customFields}
                  onValueChange={handleInputFieldChange}
                  setErrors={setErrors}
                />
              </>
            ) : (
              !isInError("record") && (
                <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
              )
            )}
            <VSpace space={4} />
          </div>
          {record && (
            <>
              <WarningModal
                title={i18n("global.ClearLines")}
                message={i18n("global.clearLinesMessage")}
                open={clearOutputLines}
                onOk={doClearOutputLines}
                onClose={() => setClearOutputLines(false)}
              />
              <WarningModal
                title={i18n("global.ClearLines")}
                message={i18n("global.clearLinesMessage")}
                open={clearInputLines}
                onOk={doClearInputLines}
                onClose={() => setClearInputLines(false)}
              />
            </>
          )}
        </Layout>
      </ObjectEditDialogContent>
    </>
  );
}
